import React from 'react';
import {
  Action,
  ActionDropDown,
  ActionIconDropdown,
  Card, CardSkeleton, HStack, ICON_CASH_CHECK, ICON_CHECK, ICON_CUSTOM_SORT_PLUS, ICON_SORT, Message,
  Select,
  Stack,
  SimpleTable,
  SimpleTableSkeleton,
  Spacing,
} from '@doveit/bricks';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { useSerpInvoices } from '../../hooks/use-serp-invoices/useSerpInvoices';
import usePagination from '../../../hooks/use-pagination/usePagination';
import {
  INVOICE_SERP_PAGE_ALL_INVOICES_FILTER_OPTION,
  INVOICE_SERP_PAGE_DEFAULT_SORT_OPTION,
  INVOICE_SERP_PAGE_FILTER_OPTIONS,
  INVOICE_SERP_PAGE_FILTERS_MAPPER,
  INVOICE_SERP_PAGE_GROUPED_FILTER_OPTIONS,
  INVOICE_SERP_PAGE_SORT_OPTIONS,
  LOAD_SERP_INVOICES_ERROR_MESSAGE,
  NO_SERP_INVOICES,
} from './constants';
import PaginatedList from '../../../components/paginated-list/PaginatedList';

import ViewSerpInvoiceTableRow from '../../containers/view-serp-invoice-table-row/ViewSerpInvoiceTableRow';
import ViewSerpInvoiceTableCard from '../../containers/view-serp-invoice-table-card/ViewSerpInvoiceTableCard';
import useFilters from '../../../hooks/use-filters/useFilters';
import { InvoiceSerpPageFilterKey } from './types';
import SearchByQueryAction from '../../../components/search-by-query-action/SearchByQueryAction';
import SerpFiltersSummary from '../../../components/serp-filters-summary/SerpFiltersSummary';
import AgentFilterAction from '../../../components/agent-filter-action/AgentFilterAction';
import { useAgentFilter } from '../../../hooks/agent-filter/useAgentFilter';
import getShortAgentName from '../../../utils/agent/getShortAgentName';

const InvoiceSerpPage: React.FC = () => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useIsDevice('mobile');

  const {
    filters,
    addFilters,
    removeFilter,
    removeFilters,
  } = useFilters(INVOICE_SERP_PAGE_FILTERS_MAPPER);

  const {
    paginationQueryParam,
    page,
    goToPage,
  } = usePagination('page', scrollRef);

  const {
    agentEmails: agents,
    replaceFilter: replaceAgentFilter,
    isLoading: isAgentFilterLoading,
    isExplicit: isAgentFilterExplicit,
  } = useAgentFilter(paginationQueryParam, getShortAgentName);
  const agentEmail = React.useMemo(() => agents.map((it) => it.email || ''), [agents]);

  const {
    data: serpInvoices,
    isLoading: areSerpInvoicesLoading,
    error: serpInvoicesError,
    mutate: mutateSerpInvoices,
  } = useSerpInvoices(isAgentFilterLoading
    ? undefined
    : {
      q: filters?.q,
      agentEmail,
      ...(INVOICE_SERP_PAGE_FILTER_OPTIONS.find(({
        key,
      }) => key === filters.filter)?.value),
    }, {
    page,
    sort: INVOICE_SERP_PAGE_SORT_OPTIONS.find(({ key }) => key === filters.sort)?.value,
  });

  const pageTitle = React.useMemo(() => {
    let title = 'Pagamenti';

    const activeFilter = INVOICE_SERP_PAGE_FILTER_OPTIONS.find(({ key }) => key === filters.filter);

    if (activeFilter && activeFilter.key !== 'all') {
      title = activeFilter.label.long;
    }

    if (serpInvoices && serpInvoices.totalElements > 0) {
      title += ` (${serpInvoices.totalElements})`;
    }

    return title;
  }, [filters.filter, serpInvoices]);

  const handleFilterBadgeClick = React.useCallback((badgeFilter: InvoiceSerpPageFilterKey) => () => {
    removeFilter(paginationQueryParam);
    addFilters({ filter: badgeFilter });
  }, [addFilters, paginationQueryParam, removeFilter]);

  const handleSearch = React.useCallback((query: string) => {
    if (query) {
      addFilters({ q: query }, { deleteKeys: [paginationQueryParam] });
    } else {
      removeFilters(['q', paginationQueryParam]);
    }
  }, [addFilters, paginationQueryParam, removeFilters]);

  return (
    <SimplePageLayout
      toolbar={(
        <HStack
          alignItems="center"
          justifyContent="space-between"
          gap={100}
        >
          <div style={{ flexGrow: '1' }}>
            {!isMobile && (
              <HStack aria-label="Filtri (desktop)" gap={150}>
                {INVOICE_SERP_PAGE_GROUPED_FILTER_OPTIONS.map(({ label, options }) => (
                  <ActionDropDown
                    key={label}
                    label={label}
                    aria-label={`Filtra pagamenti per ${label}`}
                    emphasis="low"
                    color={options.some(({ key }) => key === filters.filter) ? 'primary' : 'neutral'}
                    options={options.map(({ key, label: optionLabel }) => ({
                      key,
                      label: optionLabel.short,
                      color: filters.filter !== key ? 'neutral' : 'primary',
                      iconRight: filters.filter !== key ? undefined : { path: ICON_CHECK },
                      onClick: filters.filter !== key ? handleFilterBadgeClick(key) : undefined,
                    }))}
                  />
                ))}

                <Action
                  label={INVOICE_SERP_PAGE_ALL_INVOICES_FILTER_OPTION.label.short}
                  aria-label="Tutti i pagamenti"
                  onClick={filters.filter !== INVOICE_SERP_PAGE_ALL_INVOICES_FILTER_OPTION.key
                    ? handleFilterBadgeClick(INVOICE_SERP_PAGE_ALL_INVOICES_FILTER_OPTION.key)
                    : undefined}
                  color={filters.filter !== INVOICE_SERP_PAGE_ALL_INVOICES_FILTER_OPTION.key
                    ? 'neutral'
                    : 'primary'}
                />
              </HStack>
            )}
            {isMobile && (
              <Select
                value={filters.filter}
                aria-label="Seleziona un filtro per i pagamenti (mobile)"
                options={INVOICE_SERP_PAGE_FILTER_OPTIONS.map(({ label, key }) => ({
                  label: label.long,
                  value: key,
                }))}
                size="S"
                onChange={({ target }) => handleFilterBadgeClick(target.value as InvoiceSerpPageFilterKey)()}
              />
            )}
          </div>

          <HStack
            alignItems="center"
            gap={100}
          >
            <SearchByQueryAction
              initialQuery={filters.q}
              onSearch={handleSearch}
              width="20rem"
              placeholder="Cerca per intestatario, immobile o fattura..."
              size={!isMobile ? 'M' : 'S'}
              variant={!isMobile ? 'input' : 'action'}
            />

            <AgentFilterAction
              size={!isMobile ? 'M' : 'S'}
              onSubmit={(emails) => replaceAgentFilter(emails)()}
            />

            <ActionIconDropdown
              label="Ordina"
              aria-label="Pulsante per selezionare l'ordinamento dei pagamenti"
              placement="bottom-end"
              icon={{ path: !filters.sort || filters.sort !== INVOICE_SERP_PAGE_DEFAULT_SORT_OPTION.key ? ICON_CUSTOM_SORT_PLUS : ICON_SORT }}
              size={!isMobile ? 'M' : 'S'}
              color={filters.sort && filters.sort !== INVOICE_SERP_PAGE_DEFAULT_SORT_OPTION.key ? 'primary' : 'neutral'}
              emphasis="low"
              options={INVOICE_SERP_PAGE_SORT_OPTIONS.map(({ key, label }) => ({
                key,
                label,
                iconRight:
                  (!filters.sort && key === INVOICE_SERP_PAGE_DEFAULT_SORT_OPTION.key)
                    || filters.sort === key ? { path: ICON_CHECK } : undefined,
                color:
                  key !== INVOICE_SERP_PAGE_DEFAULT_SORT_OPTION.key
                    && filters.sort === key
                    ? 'primary'
                    : 'neutral',
                onClick: () => addFilters({ sort: key }),
              }))}
            />
          </HStack>
        </HStack>
      )}
    >
      <Card paddingSize="M">
        <Card.Header
          icon={{ path: ICON_CASH_CHECK }}
          title={pageTitle}
        />

        <Card.Content>
          <SerpFiltersSummary
            aria-label="Sommario filtri"
            filters={{ ...filters, agentFilters: { values: agents, isExplicit: isAgentFilterExplicit } }}
            sortOptions={INVOICE_SERP_PAGE_SORT_OPTIONS}
          />
          <Spacing
            aria-label="Lista pagamenti"
            margin={[300, 0, 0]}
          >
            {!isMobile && areSerpInvoicesLoading && (
              <SimpleTableSkeleton aria-label="Pagamenti in caricamento (desktop)" />
            )}

            {isMobile && areSerpInvoicesLoading && (
              <Stack aria-label="Pagamenti in caricamento (mobile)">
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </Stack>
            )}

            {serpInvoicesError && (
              <Message
                message={LOAD_SERP_INVOICES_ERROR_MESSAGE}
                type="critical"
                boxed
              />
            )}

            {(serpInvoices && serpInvoices.totalElements === 0) && (
              <Message
                message={NO_SERP_INVOICES}
                boxed
              />
            )}

            {serpInvoices && serpInvoices.totalElements > 0 && (
              <PaginatedList
                {...serpInvoices}
                hideResultCount
                goToPage={goToPage}
              >
                {!isMobile && (
                  <SimpleTable aria-label="Lista pagamenti (destkop)">
                    <SimpleTable.Body>
                      {serpInvoices.content.map((serpInvoice) => (
                        <SimpleTable.Row
                          asChild
                          key={serpInvoice.invoice.id}
                        >
                          <ViewSerpInvoiceTableRow
                            serpInvoice={serpInvoice}
                            canEdit
                            onInvoiceUpdated={() => mutateSerpInvoices()}
                          />
                        </SimpleTable.Row>
                      ))}
                    </SimpleTable.Body>
                  </SimpleTable>
                )}

                {isMobile && serpInvoices.content.map((serpInvoice) => (
                  <ViewSerpInvoiceTableCard
                    key={serpInvoice.invoice.id}
                    serpInvoice={serpInvoice}
                    canEdit
                    onInvoiceUpdated={() => mutateSerpInvoices()}
                  />
                ))}
              </PaginatedList>
            )}
          </Spacing>
        </Card.Content>
      </Card>
    </SimplePageLayout>
  );
};

export default InvoiceSerpPage;
